import {
    pathAChallenges,
    pathBChallenges,
    pathCChallenges,
    pathDChallenges,
    ChallengeOption
} from './challengeOptions';

export type OnboardingStep = 'role' | 'focus' | 'challenge' | 'style' | 'summary' | 'generating';

export interface OnboardingOption {
    id: string;
    label: string;
    description?: string;
    icon?: string;
}

export interface OnboardingQuestion {
    id: OnboardingStep;
    title: string;
    subtitle: string;
    options: OnboardingOption[];
}

// Static questions
export const roleQuestion: OnboardingQuestion = {
    id: 'role',
    title: "First, what's your role?",
    subtitle: "We'll tailor the language and examples to fit your day-to-day.",
    options: [
        { id: 'executive', label: 'Executive Director / CEO', description: 'Leading strategy, board, and vision.' },
        { id: 'program', label: 'Program Director', description: 'Managing impact, staff, and delivery.' },
        { id: 'comms', label: 'Communications / Development', description: 'Fundraising, marketing, and storytelling.' },
        { id: 'ops', label: 'Operations / HR', description: 'Systems, finance, and people.' },
    ]
};

export const focusQuestion: OnboardingQuestion = {
    id: 'focus',
    title: "What's your primary focus right now?",
    subtitle: "Choose the area where you want to see immediate AI impact.",
    options: [
        { id: 'Communication/Fundraising', label: 'Fundraising & Comms', description: 'Donor emails, grants, social posts.' },
        { id: 'Programs/Projects', label: 'Program Impact', description: 'Data analysis, reporting, curriculum.' },
        { id: 'Efficiency/Systems', label: 'Operational Efficiency', description: 'Inbox zero, meeting notes, scheduling.' },
        { id: 'Assessment', label: 'Not sure yet', description: 'Help me figure out where to start.' },
    ]
};

export const styleQuestion: OnboardingQuestion = {
    id: 'style',
    title: "How do you want to start?",
    subtitle: "Pick a pace that fits your current bandwidth.",
    options: [
        { id: 'quick_win', label: 'Quick Win (5 min)', description: 'Give me one tool I can use today.' },
        { id: 'deep_dive', label: 'Deep Dive (20 min)', description: 'I want to master a full workflow.' },
        { id: 'exploration', label: 'Open Exploration', description: 'Let me browse the full catalog.' },
    ]
};

// Dynamic Challenge Question Generator
export const getChallengeQuestion = (focusId: string): OnboardingQuestion => {
    let challenges: Record<string, ChallengeOption> = {};

    switch (focusId) {
        case 'Communication/Fundraising':
            challenges = pathAChallenges;
            break;
        case 'Programs/Projects':
            challenges = pathBChallenges;
            break;
        case 'Efficiency/Systems':
            challenges = pathCChallenges;
            break;
        case 'Assessment':
            challenges = pathDChallenges;
            break;
        default:
            challenges = pathAChallenges; // Fallback
    }

    const options: OnboardingOption[] = Object.values(challenges).map(c => ({
        id: c.id, // e.g., 'A1'
        label: c.title,
        description: c.problem
    }));

    return {
        id: 'challenge',
        title: "What's your biggest hurdle?",
        subtitle: "We'll find the specific tool to clear this blocker.",
        options
    };
};

export const onboardingQuestions = {
    role: roleQuestion,
    focus: focusQuestion,
    style: styleQuestion
};
